<template>
    <Teleport to="body">
        <div
            v-if="isOpen"
            class="modal-overlay"
            @click.self="handleClose"
            @keydown.esc="handleClose"
        >
            <div
                class="modal-card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
                ref="modalCard"
            >
                <!-- Title bar -->
                <div class="modal-header">
                    <h2
                        id="modal-title"
                        class="modal-title"
                    >
                        User Details
                    </h2>
                    <button
                        ref="closeButton"
                        class="close-button"
                        aria-label="Close modal"
                        @click="handleClose"
                        @keydown.enter="handleClose"
                        @keydown.space.prevent="handleClose"
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M18 6L6 18M6 6L18 18"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                <!-- Main content -->
                <div class="modal-content">
                    <span class="modal-text">Select the user details to display in the table and timeline.</span>
                    <div class="checkbox-row">
                        <label class="checkbox-label">
                            <input
                                ref="firstFocusable"
                                v-model="userIdSelected"
                                type="checkbox"
                                class="checkbox-input"
                                aria-label="Employee ID"
                                @keydown.enter.prevent="toggleUserId"
                                @keydown.space.prevent="toggleUserId"
                            >
                            <span class="checkbox-text">Employee ID</span>
                        </label>
                    </div>
                    <div class="checkbox-row">
                        <label class="checkbox-label">
                            <input
                                v-model="emailSelected"
                                type="checkbox"
                                class="checkbox-input"
                                aria-label="Email"
                                @keydown.enter.prevent="toggleEmail"
                                @keydown.space.prevent="toggleEmail"
                            >
                            <span class="checkbox-text">Email</span>
                        </label>
                    </div>
                </div>

                <!-- Actions bar -->
                <div class="modal-footer">
                    <button
                        class="btn-cancel"
                        @click="handleCancel"
                        @keydown.enter="handleCancel"
                    >
                        Cancel
                    </button>
                    <button
                        ref="lastFocusable"
                        class="btn-save"
                        @click="handleSave"
                        @keydown.enter="handleSave"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import {
    ref, watch, nextTick, onUnmounted
} from 'vue';

interface Props {
    isOpen?: boolean;
    initialUserId?: boolean;
    initialEmail?: boolean;
}

interface Emits {
    (e: 'close'): void;
    (e: 'save', data: { userId: boolean; email: boolean }): void;
    (e: 'cancel'): void;
}

const props = withDefaults(defineProps<Props>(), {
    isOpen: false,
    initialUserId: true,
    initialEmail: true
});

const emit = defineEmits<Emits>();

const userIdSelected = ref(true);
const emailSelected = ref(true);
const modalCard = ref<HTMLElement | null>(null);
const firstFocusable = ref<HTMLElement | null>(null);
const lastFocusable = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLElement | null>(null);
let previousActiveElement: HTMLElement | null = null;

const toggleUserId = () => { userIdSelected.value = !userIdSelected.value; };
const toggleEmail = () => { emailSelected.value = !emailSelected.value; };

// Focus trap handler
const handleFocusTrap = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusableElements = modalCard.value?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusableElements || focusableElements.length === 0) return;
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    if (e.shiftKey) {
        if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        }
    } else {
        if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }
    }
};

watch(() => props.isOpen, async (newValue) => {
    if (newValue) {
        userIdSelected.value = props.initialUserId;
        emailSelected.value = props.initialEmail;
        previousActiveElement = document.activeElement as HTMLElement;
        await nextTick();
        if (firstFocusable.value) firstFocusable.value.focus();
        document.addEventListener('keydown', handleFocusTrap);
    } else {
        document.removeEventListener('keydown', handleFocusTrap);
        if (previousActiveElement) previousActiveElement.focus();
    }
});

onUnmounted(() => { document.removeEventListener('keydown', handleFocusTrap); });

const handleClose = () => { emit('close'); };
const handleCancel = () => { emit('cancel'); emit('close'); };
const handleSave = () => {
    emit('save', { userId: userIdSelected.value, email: emailSelected.value });
    emit('close');
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.modal-card {
    width: 457px;
    height: 282px;
    background: #FFFFFF;
    border: 1px solid #DCE3E4;
    border-radius: 4px;
    box-shadow: 0px 16px 12px -4px rgba(17, 66, 62, 0.1);
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.modal-header {
    height: 76px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 24px;
    gap: 16px;
    box-shadow: inset 0px -1px 0px #E8EDED;
}

.modal-title {
    font-family: 'Roboto', sans-serif;
    font-weight: 700;
    font-size: 20px;
    line-height: 1.2;
    color: #1A2021;
    margin: 0;
}

.close-button {
    width: 24px;
    height: 24px;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    color: #535859;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
    border-radius: 4px;
}
.close-button:hover { color: #1A2021; }
.close-button:focus, .close-button:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
    color: #000;
}

.modal-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0 24px;
    flex: 1;
    height: 90px;
    justify-content: center;
}
.modal-text {
    font-family: 'Roboto';
    font-weight: 400;
    font-size: 14px;
    color: #1A2021;
}

.checkbox-row {
    display: flex;
    align-items: center;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
}

.checkbox-input {
    width: 24px;
    height: 24px;
    border: 2px solid #DCE3E4;
    border-radius: 4px;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: #FFFFFF;
    position: relative;
    transition: all 0.2s;
}
.checkbox-input:checked {
    background-color: #2E76F6;
    border-color: #2E76F6;
}
.checkbox-input:checked::after {
    content: '';
    position: absolute;
    left: 7px;
    top: 3px;
    width: 6px;
    height: 11px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}
.checkbox-input:hover { border-color: #2E76F6; }
.checkbox-input:focus, .checkbox-input:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
    border-color: #000;
}

.checkbox-text {
    font-family: 'Roboto';
    font-weight: 400;
    font-size: 16px;
    line-height: 1.5;
    color: #1A2021;
}

.modal-footer {
    height: 84px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 20px 24px;
    gap: 12px;
    box-shadow: inset 0px 1px 0px #E8EDED;
}

.btn-cancel, .btn-save {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    line-height: 1.5;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}
.btn-cancel {
    background: #FFFFFF;
    border: 1px solid #31708F;
    color: #31708F;
}
.btn-cancel:hover { background: #F5F9FA; }
.btn-cancel:focus, .btn-cancel:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
}
.btn-save {
    background: #31708F;
    color: #FFFFFF;
}
.btn-save:hover { background: #275A73; }
.btn-save:focus, .btn-save:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
}
</style>
